<template>
	<article
		class="bm-card"
		:class="{'bm-card--edit': editMode, 'bm-card--dragging': dragging}"
		:draggable="editMode"
		@dragstart="handleDragStart"
		@dragover.prevent
		@drop.stop.prevent="handleDrop"
		@dragend="$emit('drag-end')"
	>
		<button type="button" class="bm-card__main" :title="bookmark.description || bookmark.url" @click="open">
			<span class="bm-card__icon" :class="{'bm-card__icon--emoji': textIcon}">
				<img v-if="iconSrc" :src="iconSrc" alt="" @error="handleIconError">
				<span v-else-if="textIcon" class="bm-card__emoji">{{ textIcon }}</span>
				<span v-else class="bm-card__letter" :style="{background: letterColor}">{{ letter }}</span>
			</span>
			<span class="bm-card__title">{{ bookmark.title }}</span>
		</button>
		<div v-if="editMode" class="bm-card__actions">
			<button type="button" class="bm-card__action bm-card__action--edit" title="编辑" @click.stop="$emit('edit', bookmark)">
				<span aria-hidden="true">✎</span>
				<span class="bm-card__action-text">编辑</span>
			</button>
			<button type="button" class="bm-card__action bm-card__action--remove" title="删除" @click.stop="$emit('remove', bookmark)">
				<span aria-hidden="true">✕</span>
				<span class="bm-card__action-text">删除</span>
			</button>
		</div>
	</article>
</template>

<script>
	export default {
		name: 'BookmarkCard',
		props: {
			/**
			 * 书签数据
			 */
			bookmark: {
				type: Object,
				required: true
			},
			/**
			 * 是否处于编辑模式
			 */
			editMode: {
				type: Boolean,
				default: false
			},
			/**
			 * 是否正在被拖拽
			 */
			dragging: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				iconFailed: false
			}
		},
		computed: {
			/**
			 * 图标地址：自定义图标优先，其次取站点 favicon
			 */
			iconSrc() {
				if (this.iconFailed) {
					return ''
				}
				const customIcon = (this.bookmark.icon || '').trim()
				if (customIcon) {
					return /^(http|data):/i.test(customIcon) ? customIcon : ''
				}
				try {
					const parsed = new URL(this.bookmark.url)
					return `${parsed.protocol}//${parsed.host}/favicon.ico`
				} catch (e) {
					return ''
				}
			},
			/**
			 * 文本图标（emoji 等），优先于站点 favicon
			 */
			textIcon() {
				const customIcon = (this.bookmark.icon || '').trim()
				if (!customIcon || /^(http|data):/i.test(customIcon)) {
					return ''
				}
				return customIcon
			},
			/**
			 * 图标加载失败时的首字母
			 */
			letter() {
				const title = (this.bookmark.title || '').trim()
				return title ? title.charAt(0).toUpperCase() : '?'
			},
			/**
			 * 首字母色块背景，按标题哈希取色，保证同一书签颜色稳定
			 */
			letterColor() {
				const title = this.bookmark.title || ''
				let hash = 0
				for (let i = 0; i < title.length; i++) {
					hash = title.charCodeAt(i) + ((hash << 5) - hash)
				}
				const hue = Math.abs(hash) % 360
				return `linear-gradient(135deg, hsl(${hue}, 68%, 72%), hsl(${(hue + 28) % 360}, 66%, 58%))`
			}
		},
		watch: {
			/**
			 * 书签变化时重置图标加载状态
			 */
			bookmark() {
				this.iconFailed = false
			}
		},
		methods: {
			/**
			 * 打开书签
			 *
			 * <p>编辑模式下不跳转，避免误触。</p>
			 */
			open() {
				if (this.editMode) {
					return
				}
				this.$emit('open', this.bookmark)
			},
			/**
			 * 图标加载失败后切换为首字母色块
			 */
			handleIconError() {
				this.iconFailed = true
			},
			/**
			 * 开始拖拽书签
			 *
			 * @param {DragEvent} event 拖拽事件
			 */
			handleDragStart(event) {
				if (!this.editMode) {
					return
				}
				if (event.dataTransfer) {
					event.dataTransfer.effectAllowed = 'move'
					event.dataTransfer.setData('text/plain', String(this.bookmark.id))
				}
				this.$emit('drag-start', this.bookmark)
			},
			/**
			 * 书签被放置到当前卡片上
			 *
			 * @param {DragEvent} event 拖拽事件，供父组件计算插入方向
			 */
			handleDrop(event) {
				this.$emit('drop', this.bookmark, event)
			}
		}
	}
</script>

<style scoped>
	.bm-card {
		position: relative;
		display: flex;
		flex-direction: column;
		border-radius: 20px;
		background: rgba(255, 255, 255, 0.055);
		border: 1px solid rgba(255, 255, 255, 0.08);
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 10px 24px rgba(3, 6, 20, 0.28);
		overflow: hidden;
		transition: transform 0.25s cubic-bezier(0.22, 0.68, 0.36, 1), background 0.25s ease,
			box-shadow 0.25s ease, border-color 0.25s ease;
	}

	.bm-card:hover {
		transform: translateY(-4px);
		background: rgba(255, 255, 255, 0.12);
		border-color: rgba(150, 175, 255, 0.32);
		box-shadow: 0 18px 36px rgba(3, 6, 20, 0.45), 0 0 0 1px rgba(150, 175, 255, 0.16);
	}

	.bm-card--edit {
		cursor: grab;
	}

	.bm-card--edit .bm-card__main {
		padding-bottom: 8px;
	}

	.bm-card--dragging {
		opacity: 0.4;
		transform: scale(0.96);
	}

	.bm-card__main {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
		padding: 18px 12px 14px;
		border: none;
		background: transparent;
		color: inherit;
		cursor: pointer;
		font: inherit;
	}

	.bm-card__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 50px;
		height: 50px;
		margin-bottom: 10px;
		border-radius: 16px;
		background: rgba(255, 255, 255, 0.92);
		box-shadow: 0 10px 22px rgba(3, 6, 20, 0.35), inset 0 0 0 1px rgba(255, 255, 255, 0.6);
		overflow: hidden;
		transition: transform 0.25s cubic-bezier(0.22, 0.68, 0.36, 1), box-shadow 0.25s ease;
	}

	.bm-card:hover .bm-card__icon {
		transform: scale(1.08) rotate(-3deg);
		box-shadow: 0 14px 28px rgba(3, 6, 20, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.75);
	}

	.bm-card__icon img {
		width: 28px;
		height: 28px;
		object-fit: contain;
	}

	.bm-card__icon--emoji {
		background: linear-gradient(135deg, rgba(120, 150, 255, 0.3), rgba(190, 110, 255, 0.3));
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16), 0 10px 22px rgba(3, 6, 20, 0.28);
	}

	.bm-card__emoji {
		font-size: 26px;
		line-height: 1;
	}

	.bm-card__letter {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		color: #fff;
		font-size: 20px;
		font-weight: 600;
	}

	.bm-card__title {
		display: -webkit-box;
		overflow: hidden;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		max-width: 100%;
		color: rgba(232, 238, 255, 0.86);
		font-size: 13px;
		line-height: 1.35;
		text-align: center;
		word-break: break-word;
	}

	.bm-card:hover .bm-card__title {
		color: #fff;
	}

	.bm-card__actions {
		flex: none;
		display: flex;
		gap: 6px;
		padding: 0 10px 10px;
	}

	.bm-card__action {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		flex: 1;
		min-width: 0;
		height: 32px;
		padding: 0 6px;
		border: 1px solid rgba(120, 150, 255, 0.45);
		border-radius: 11px;
		background: rgba(109, 125, 255, 0.28);
		color: #e3e9ff;
		cursor: pointer;
		font-size: 12px;
		font-weight: 500;
		line-height: 1;
		transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
	}

	.bm-card__action--edit:hover {
		background: linear-gradient(135deg, #6d7dff, #8d7bff);
		border-color: transparent;
		color: #fff;
		transform: translateY(-1px);
		box-shadow: 0 8px 18px rgba(109, 125, 255, 0.4);
	}

	.bm-card__action--remove {
		border-color: rgba(255, 120, 145, 0.45);
		background: rgba(255, 80, 110, 0.24);
		color: #ffd9e0;
	}

	.bm-card__action--remove:hover {
		background: linear-gradient(135deg, #f0506e, #ff7a92);
		border-color: transparent;
		color: #fff;
		transform: translateY(-1px);
		box-shadow: 0 8px 18px rgba(240, 80, 110, 0.4);
	}
</style>
